import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Stack,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  fetchMunicipalityByCode,
  fetchMunicipalityChitalishta
} from '../store/slices/municipalitySlice';
import MunicipalityMetricsTabs from '../components/municipal/MunicipalityMetricsTabs';

const MunicipalityDetailPage = () => {
  const theme = useTheme();
  const { municipalityCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentMunicipality,
    chitalishta,
    loading,
    error
  } = useSelector((state) => state.municipalities);

  useEffect(() => {
    if (municipalityCode) {
      dispatch(fetchMunicipalityByCode(municipalityCode));
      dispatch(fetchMunicipalityChitalishta(municipalityCode));
    }
  }, [municipalityCode, dispatch]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !currentMunicipality) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/municipalities')}
          sx={{ mb: 3, color: theme.palette.custom.primaryOrange }}
        >
          Назад към общините
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {error || 'Общината не е намерена'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: { xs: 2, sm: 4 },
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Заглавна част */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/municipalities')}
          sx={{ mb: 2, color: theme.palette.custom.primaryOrange, borderColor: theme.palette.custom.primaryOrange }}
          variant="outlined"
          size="small"
        >
          Назад към общините
        </Button>
        
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            background: theme.palette.custom.secondaryOrangeGradient,
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 700, 
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}>
                {currentMunicipality.municipality}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Chip 
                  label={`Област: ${currentMunicipality.district}`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.25)', 
                    color: 'white',
                    fontWeight: 500,
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' }
                  }}
                  size="small"
                />
                {currentMunicipality.municipalityPopulation && (
                  <Chip 
                    label={`Население: ${currentMunicipality.municipalityPopulation.toLocaleString()}`}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.25)', 
                      color: 'white',
                      fontWeight: 500,
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' }
                    }}
                    size="small"
                  />
                )}
              </Stack>
            </Box>

            {/* Бързи статистики */}
            {chitalishta && chitalishta.length > 0 && (
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 } }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    lineHeight: 1,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}>
                    {chitalishta.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                    Читалища
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Табове с показатели - пълна ширина */}
      <MunicipalityMetricsTabs municipalityCode={municipalityCode} />
    </Container>
  );
};

export default MunicipalityDetailPage;